import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Download, CreditCard, FileText, Gift, Heart, Star, ArrowRight, Camera } from "lucide-react";
import { img } from "../lib/api";
import { useSite } from "../lib/store";
import { Dots, sections } from "../components/Bits";
import { PageBanner, CtaBand, TINTS, INKS } from "./HowItWorks";

const ICONS = [Download, CreditCard, FileText, Gift, Heart, Star];

function Owner({ t }) {
  return (
    <section className="max-w-7xl mx-auto px-6 py-16" data-testid="about-owner">
      <div className="relative grid lg:grid-cols-[1fr_1.2fr] gap-12 items-center bg-[var(--blush)] sketch p-8 sm:p-14 overflow-hidden">
        <Dots />
        <div className="relative mx-auto w-full max-w-sm">
          {t.about_photo ? (
            <img src={img(t.about_photo)} alt={t.about_signature} className="w-full aspect-[4/5] object-cover rounded-[36px] border-8 border-white shadow-lg rotate-2" data-testid="about-owner-photo" />
          ) : (
            <div className="w-full aspect-[4/5] rounded-[36px] border-4 border-dashed border-[var(--pink)] bg-white grid place-items-center text-center p-6 rotate-2" data-testid="about-owner-photo-placeholder">
              <div><Camera className="w-12 h-12 mx-auto text-[var(--pink)]" /><p className="font-hand text-lg text-[var(--muted)] mt-2">Místo pro fotku</p><p className="text-xs text-[var(--muted)]">nahrajete v administraci</p></div>
            </div>
          )}
          <span className="absolute -bottom-4 -right-2 font-hand text-2xl bg-[var(--sun)] px-5 py-1 rounded-full -rotate-3 shadow">{t.about_signature}</span>
        </div>
        <div className="relative">
          <p className="eyebrow mb-2">Majitelka</p>
          <h2 className="text-3xl sm:text-4xl font-semibold mb-6"><span className="squiggle">{t.about_owner_title}</span></h2>
          <div className="space-y-4 text-[#4a4752] leading-relaxed whitespace-pre-line" data-testid="about-owner-text">{t.about_owner_text}</div>
        </div>
      </div>
    </section>
  );
}

function System({ t }) {
  return (
    <section className="max-w-7xl mx-auto px-6 py-12" data-testid="about-system">
      <p className="eyebrow mb-2 text-center">Celý systém</p>
      <h2 className="text-3xl sm:text-4xl font-semibold text-center mb-12"><span className="squiggle">{t.about_system_title}</span></h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {sections(t.about_system).map((s, i) => {
          const I = ICONS[i % ICONS.length];
          return (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: (i % 3) * 0.08 }}
              className="rounded-[28px] p-7 border border-[#f6e3ec] bg-white hover:-translate-y-1 hover:shadow-[0_20px_40px_-25px_rgba(212,86,140,.4)] transition-[transform,box-shadow]" data-testid={`about-feature-${i}`}>
              <span className="w-14 h-14 rounded-2xl grid place-items-center" style={{ background: TINTS[i % 4] }}><I className="w-6 h-6" style={{ color: INKS[i % 4] }} /></span>
              <h3 className="font-display text-xl mt-5">{s.title}</h3>
              <p className="text-sm text-[var(--muted)] mt-2 leading-relaxed">{s.body}</p>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}

import { useSeo } from "../components/Seo";

export default function About() {
  const { site } = useSite();
  useSeo({ title: site?.texts.about_hero_title || "O nás", description: site?.texts.about_hero_text || site?.texts.about_owner_text, image: site?.texts.about_photo || undefined });
  if (!site) return <div className="h-[60vh]" />;
  const t = site.texts;
  return (
    <div data-testid="about-page">
      <PageBanner testid="about-banner" eyebrow="O nás" title={t.about_hero_title} text={t.about_hero_text}>
        <Link to="/eshop" className="btn-pink mt-8">Prohlédnout materiály <ArrowRight className="w-4 h-4" /></Link>
      </PageBanner>
      <Owner t={t} />
      <System t={t} />
      {t.about_page && (
        <section className="max-w-3xl mx-auto px-6 py-10 prose-mps whitespace-pre-line" data-testid="about-story">
          {t.about_page.split(/\n(?=## )/).map((b, i) => {
            const [h, ...r] = b.split("\n");
            return h.startsWith("## ") ? <div key={i}><h2>{h.slice(3)}</h2><p>{r.join("\n").trim()}</p></div> : <p key={i}>{b}</p>;
          })}
        </section>
      )}
      <CtaBand />
    </div>
  );
}
